import { Befehl95 } from '../../../models'
import { Befehl95FormData, Befehl95FormSchema } from './schema'
import {
  Befehl95DataDto,
  BefehlAuftrag_95_10Dto,
  BefehlAuftrag_95_20Dto,
  BefehlAuftrag_95_30Dto,
  BefehlAuftrag_95_40Dto,
  BefehlAuftrag_95_50Dto,
  BefehlAuftrag_95_53Dto,
  BefehlAuftrag_95_54Dto,
  BefehlAuftrag_95_95Dto
} from '../../../dtos/befehl-95-dto'
import {
  createFreitextAuftrag,
  createKmField,
  createSignalWithZugmeldestelleField,
  createSimpleAuftrag,
  findAuftragByKennung,
  hasAuftrag
} from '../converter-utils'

export const convertModelToBefehl95FormData = (
  befehl?: Befehl95
): Befehl95FormData => {
  const formData = Befehl95FormSchema.parse({})

  if (!befehl?.auftraege) {
    return formData
  }

  if (hasAuftrag(befehl.auftraege, '95.10')) {
    formData.auftrag_95_10.enabled = true
  } else {
    formData.auftrag_95_10.enabled = false
  }

  if (hasAuftrag(befehl.auftraege, '95.20')) {
    formData.auftrag_95_20.enabled = true
  } else {
    formData.auftrag_95_20.enabled = false
  }

  if (hasAuftrag(befehl.auftraege, '95.30')) {
    formData.auftrag_95_30.enabled = true
  } else {
    formData.auftrag_95_30.enabled = false
  }

  if (hasAuftrag(befehl.auftraege, '95.40')) {
    formData.auftrag_95_40.enabled = true
  } else {
    formData.auftrag_95_40.enabled = false
  }

  if (hasAuftrag(befehl.auftraege, '95.53')) {
    formData.auftrag_95_53.enabled = true
  } else {
    formData.auftrag_95_53.enabled = false
  }

  if (hasAuftrag(befehl.auftraege, '95.54')) {
    formData.auftrag_95_54.enabled = true
  } else {
    formData.auftrag_95_54.enabled = false
  }

  handleAuftrag_95_50(befehl, formData)

  handleAuftrag_95_95(befehl, formData)

  return formData
}

function handleAuftrag_95_50(befehl: Befehl95, formData: Befehl95FormData) {
  const auftrag95_50 = findAuftragByKennung(
    befehl.auftraege,
    '95.50'
  ) as BefehlAuftrag_95_50Dto
  if (auftrag95_50) {
    formData.auftrag_95_50.enabled = true
    for (const feld of auftrag95_50.felder) {
      switch (feld.kennung) {
        case '95.51':
          formData.auftrag_95_50.type = 'km'
          formData.auftrag_95_50.km = feld.werte?.km || ''
          // UI sets this based on type but tests expect it
          formData.auftrag_95_50.in_am = 'in'
          break
        case '95.52':
          formData.auftrag_95_50.type = 'signal_zugmeldestelle'
          formData.auftrag_95_50.signal = feld.werte?.signal || ''
          formData.auftrag_95_50.zugmeldestelle = {
            art: feld.werte?.zugmeldestelle?.art || '',
            name: feld.werte?.zugmeldestelle?.name || ''
          }
          // UI sets this based on type but tests expect it
          formData.auftrag_95_50.in_am = 'am'
      }
    }
  } else {
    formData.auftrag_95_50.enabled = false
    formData.auftrag_95_50.in_am = ''
    formData.auftrag_95_50.type = ''
  }
}

function handleAuftrag_95_95(befehl: Befehl95, formData: Befehl95FormData) {
  const auftrag95_95 = findAuftragByKennung(
    befehl.auftraege,
    '95.95'
  ) as BefehlAuftrag_95_95Dto
  if (auftrag95_95) {
    formData.auftrag_95_95.enabled = true
    formData.auftrag_95_95.freitext =
      auftrag95_95.felder?.[0]?.werte?.freitext || ''
  } else {
    formData.auftrag_95_95.enabled = false
    formData.auftrag_95_95.freitext = ''
  }
}

export const convertBefehl95FormDataToModel = (
  formData: Befehl95FormData,
  frontendId: string
): Befehl95 => {
  const auftraege: Befehl95DataDto = []

  // Auftrag 95.10
  const auftrag9510 = createSimpleAuftrag(
    '95.10',
    formData.auftrag_95_10.enabled
  )
  if (auftrag9510) auftraege.push(auftrag9510 as BefehlAuftrag_95_10Dto)

  // Auftrag 95.20
  const auftrag9520 = createSimpleAuftrag(
    '95.20',
    formData.auftrag_95_20.enabled
  )
  if (auftrag9520) auftraege.push(auftrag9520 as BefehlAuftrag_95_20Dto)

  // Auftrag 95.30
  const auftrag9530 = createSimpleAuftrag(
    '95.30',
    formData.auftrag_95_30.enabled
  )
  if (auftrag9530) auftraege.push(auftrag9530 as BefehlAuftrag_95_30Dto)

  // Auftrag 95.40
  const auftrag9540 = createSimpleAuftrag(
    '95.40',
    formData.auftrag_95_40.enabled
  )
  if (auftrag9540) auftraege.push(auftrag9540 as BefehlAuftrag_95_40Dto)

  // Complex auftrag 95.50
  if (formData.auftrag_95_50?.enabled) {
    const fields = []

    if (formData.auftrag_95_50.type === 'km') {
      fields.push(createKmField('95.51', formData.auftrag_95_50.km || ''))
    } else if (formData.auftrag_95_50.type === 'signal_zugmeldestelle') {
      fields.push(
        createSignalWithZugmeldestelleField(
          '95.52',
          formData.auftrag_95_50.signal || '',
          formData.auftrag_95_50.zugmeldestelle?.art || '',
          formData.auftrag_95_50.zugmeldestelle?.name || ''
        )
      )
    }

    auftraege.push({
      kennung: '95.50',
      felder: fields
    } as BefehlAuftrag_95_50Dto)
  }

  // Auftrag 95.53
  const auftrag9553 = createSimpleAuftrag(
    '95.53',
    formData.auftrag_95_53.enabled
  )
  if (auftrag9553) auftraege.push(auftrag9553 as BefehlAuftrag_95_53Dto)

  // Auftrag 95.54
  const auftrag9554 = createSimpleAuftrag(
    '95.54',
    formData.auftrag_95_54.enabled
  )
  if (auftrag9554) auftraege.push(auftrag9554 as BefehlAuftrag_95_54Dto)

  // Auftrag 95.95
  const auftrag9595 = createFreitextAuftrag(
    '95',
    formData.auftrag_95_95?.enabled,
    formData.auftrag_95_95?.freitext || ''
  )
  if (auftrag9595) auftraege.push(auftrag9595 as BefehlAuftrag_95_95Dto)

  return {
    kennung: '95',
    frontendId,
    auftraege
  }
}
